/**
 * Phase 5 — Shared academic attendance calculator (frontend).
 *
 * SINGLE SOURCE OF TRUTH for academic attendance math on the client.
 * Mirrors `/app/backend/services/academic_attendance_service.py` exactly.
 *
 * Rules:
 *   • Numerator (counts as attended):
 *       Present, Late, Late - Justified, Online, Excused
 *   • Denominator (scheduled school days):
 *       Total days minus `No Class` (school holidays / weekends w/o lessons)
 *   • Counts as missed:
 *       Absent, No Show, plus any unknown status (conservative).
 *   • `Late` and `Late - Justified` are TREATED IDENTICALLY for attendance %.
 *     They are only kept apart so the breakdown UI can show context.
 *
 * Any module that displays an attendance number, trend, or breakdown MUST
 * import from this helper to avoid drift between Trend / Panel / Dashboard /
 * Season Report / future analytics.
 */

const ATTENDED_TOKENS = new Set(['present', 'late', 'latejustified', 'online', 'excused']);
const ABSENT_TOKENS = new Set(['absent', 'noshow']);
const EXCLUDED_TOKENS = new Set(['noclass']);

const STATUS_LABELS = {
  present: 'Present',
  late: 'Late',
  latejustified: 'Late - Justified',
  online: 'Online',
  excused: 'Excused',
  absent: 'Absent',
  noshow: 'No Show',
  noclass: 'No Class',
};

/** Normalize a status string the same way the backend does. */
export const normalizeStatus = (s) =>
  String(s ?? '').trim().toLowerCase().replace(/[\s_-]/g, '');

/** Pretty label for the normalized token, or 'unknown'. */
export const prettyStatus = (s) => STATUS_LABELS[normalizeStatus(s)] || 'unknown';

/** True if the status counts toward the attendance percentage. */
export const isAttended = (s) => ATTENDED_TOKENS.has(normalizeStatus(s));

/** True if the status counts as missed. */
export const isMissed = (s) => ABSENT_TOKENS.has(normalizeStatus(s));

/** True if the status is excluded from BOTH numerator and denominator. */
export const isExcluded = (s) => EXCLUDED_TOKENS.has(normalizeStatus(s));

/**
 * Compute weekly stats from a `days` array.
 * Mirrors `compute_academic_weekly_stats(days)` from the Python service.
 *
 * @param {Array<{status: string}>|null|undefined} days
 * @returns {{
 *   total_days: number,
 *   scheduled_days: number,
 *   attended_count: number,
 *   missed_count: number,
 *   no_class_count: number,
 *   unknown_count: number,
 *   attendance_rate: number,
 *   breakdown: Record<string, number>,
 * }}
 */
export const computeAcademicWeeklyStats = (days) => {
  const list = Array.isArray(days) ? days : [];
  const breakdown = {
    Present: 0, Late: 0, 'Late - Justified': 0, Online: 0, Excused: 0,
    Absent: 0, 'No Show': 0, 'No Class': 0, unknown: 0,
  };
  let attended = 0, missed = 0, noClass = 0, unknown = 0;

  for (const d of list) {
    const s = d?.status;
    const label = prettyStatus(s);
    breakdown[label] = (breakdown[label] || 0) + 1;
    if (isAttended(s)) attended += 1;
    else if (isMissed(s)) missed += 1;
    else if (isExcluded(s)) noClass += 1;
    else unknown += 1;
  }

  const total = list.length;
  const scheduled = total - noClass;
  const rate = scheduled > 0
    ? Math.round((attended / scheduled) * 1000) / 10
    : 0;

  return {
    total_days: total,
    scheduled_days: scheduled,
    attended_count: attended,
    missed_count: missed + unknown,
    no_class_count: noClass,
    unknown_count: unknown,
    attendance_rate: rate,
    breakdown,
  };
};

/**
 * Legacy weekly fallback when a record has no `days[]` payload — derive a
 * rate from the stored weekly summary status. Matches the backend's
 * `_legacy_fallback_rate` semantics so historical records render the
 * same number everywhere.
 *
 * Returns:
 *   - null when the week should be EXCLUDED from the trend (No Class)
 *   - 0   when the week summary is Absent / No Show
 *   - 100 otherwise (Present / Late / Late - Justified / Online / Excused)
 */
export const legacyWeeklyFallbackRate = (summaryStatus) => {
  if (isExcluded(summaryStatus)) return null;
  if (isMissed(summaryStatus)) return 0;
  return 100;
};
